/**
 * Lodestar i18n bootstrap.
 *
 * Why this file is intentionally small:
 *   • All translation data lives in /locales/<code>.json — adding a new
 *     language is a single drop-in there + one line in SUPPORTED below.
 *   • The hot-path hooks (useTranslation / useLanguage) come from
 *     react-i18next directly; no custom indirection.
 *   • Language is persisted to localStorage under `lodestar.lang`. The
 *     legacy chat-only key `lodestar.chat.lang` (iter-40) is read once on
 *     first load for backwards compatibility, then dropped.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import hi from "./locales/hi.json";
import ta from "./locales/ta.json";
import es from "./locales/es.json";
import { api } from "@/lib/api";

// Drop-in language registry — `code` matches the JSON file basename and the
// $meta.code inside it. Adding a 5th language = drop /locales/<code>.json
// here PLUS add one entry to this array.
export const SUPPORTED = [
    { code: "en", bundle: en },
    { code: "hi", bundle: hi },
    { code: "ta", bundle: ta },
    { code: "es", bundle: es },
];

export const DEFAULT_LANG = "en";
export const STORAGE_KEY = "lodestar.lang";
export const LEGACY_CHAT_LANG_KEY = "lodestar.chat.lang";  // iter-40 → iter-41 migration

function detectInitialLanguage() {
    if (typeof window === "undefined") return DEFAULT_LANG;
    // 1. Explicit choice on this device.
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.some((s) => s.code === stored)) return stored;
    // 2. One-time migration from iter-40's chat-only key.
    const legacy = window.localStorage.getItem(LEGACY_CHAT_LANG_KEY);
    if (legacy && SUPPORTED.some((s) => s.code === legacy)) {
        window.localStorage.setItem(STORAGE_KEY, legacy);
        return legacy;
    }
    // 3. Closest browser language.
    const nav = (navigator?.language || "en").slice(0, 2).toLowerCase();
    return SUPPORTED.some((s) => s.code === nav) ? nav : DEFAULT_LANG;
}

const resources = Object.fromEntries(
    SUPPORTED.map((s) => [s.code, { translation: s.bundle }])
);

i18n
    .use(initReactI18next)
    .init({
        resources,
        lng: detectInitialLanguage(),
        fallbackLng: DEFAULT_LANG,
        interpolation: { escapeValue: false }, // React already escapes
        returnNull: false,
    });

// Keep <html lang="…"> in sync so screen-readers + native font fallback work.
function syncHtmlLang(code) {
    if (typeof document !== "undefined") {
        document.documentElement.lang = code;
    }
}
syncHtmlLang(i18n.language);
i18n.on("languageChanged", (code) => {
    if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, code);
    }
    syncHtmlLang(code);
    // Best-effort mirror to the user profile so the choice follows the user
    // across devices. Fails silently for anon visitors (401) — localStorage
    // is the authoritative source on the current device either way.
    try {
        api.put("/account/profile", { interface_language: code }).catch(() => {});
    } catch {
        /* api helper may not be ready during initial bootstrap */
    }
});

/**
 * Sync the current user's saved interface_language INTO the active i18n
 * instance. Called after login so a user who switched to Hindi on phone
 * sees Hindi on a fresh desktop session too. Profile value WINS over
 * localStorage when present.
 */
export async function syncFromProfile() {
    try {
        const res = await api.get("/account/profile");
        const saved = res?.data?.profile?.interface_language;
        if (saved && SUPPORTED.some((s) => s.code === saved) && saved !== i18n.language) {
            i18n.changeLanguage(saved);
        }
    } catch {
        /* anon or no profile yet — local choice rules */
    }
}

export default i18n;
